import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SubSink } from 'subsink';
import {
  IUserAllied,
  UserAlliedService,
} from 'src/app/services/user-allied.service';

@Component({
  selector: 'app-list-user-allieds',
  templateUrl: './list-user-allieds.component.html',
  styleUrls: ['./list-user-allieds.component.scss'],
})
export class ListUserAlliedsComponent implements OnInit, OnDestroy {
  @Input() public organizationIndex: string;
  @Output() hasUserAlliedChange = new EventEmitter<boolean>();

  private subs = new SubSink();

  public dataSource: IUserAllied[] = [];
  public isLoading: boolean = false;
  public processingUserId: string | null = null;
  public resendingUserId: string | null = null;
  public updatingRoleUserId: string | null = null;
  public readonly availableRoles: Array<'Standard' | 'Premium'> = ['Standard', 'Premium'];

  constructor(
    private userAlliedService: UserAlliedService,
    private _snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.getUsersByAllied();

    this.subs.sink = this.userAlliedService.Refreshrequired.subscribe(() => {
      this.getUsersByAllied();
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public get hasUserAllied(): boolean {
    return this.dataSource.length > 0;
  }

  public getUsersByAllied(): void {
    if (!this.organizationIndex) {
      return;
    }

    this.isLoading = true;

    this.subs.sink = this.userAlliedService
      .getUsersByAllied(this.organizationIndex)
      .subscribe({
        next: (response) => {
          this.dataSource = response?.data || [];
          this.isLoading = false;

          this.hasUserAlliedChange.emit(this.dataSource.length > 0);
        },
        error: (error) => {
          this.isLoading = false;
          this.dataSource = [];

          this.hasUserAlliedChange.emit(false);

          const backendMessage =
            error?.error?.message ||
            error?.error?.errors?.[0]?.message ||
            'Ups! error al obtener cuentas';

          this._snackBar.open(backendMessage, 'cerrar', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: 'my-snackbar-error',
          });
        },
      });
  }

  public getRoleClass(role: string): string {
    return role === 'Premium' ? 'premium-role' : 'standard-role';
  }

  public getStatusClass(active: boolean): string {
    return active ? 'active-status' : 'inactive-status';
  }

  public resendCredentials(user: IUserAllied): void {
    if (this.resendingUserId) {
      return;
    }

    const confirmed = window.confirm(
      `¿Reenviar credenciales a ${user.email}? Se generará una contraseña nueva.`,
    );

    if (!confirmed) {
      return;
    }

    this.resendingUserId = user._id;

    this.subs.sink = this.userAlliedService
      .resendCredentials(user._id)
      .subscribe({
        next: () => {
          this.resendingUserId = null;

          this._snackBar.open(
            `Credenciales enviadas a ${user.email}`,
            'cerrar',
            {
              duration: 3500,
              horizontalPosition: 'right',
              verticalPosition: 'top',
            },
          );
        },
        error: (error) => {
          this.resendingUserId = null;

          const backendMessage =
            error?.error?.message ||
            error?.error?.errors?.[0]?.message ||
            'Ups! error al reenviar credenciales';

          this._snackBar.open(backendMessage, 'cerrar', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: 'my-snackbar-error',
          });
        },
      });
  }

  public onRoleChange(user: IUserAllied, newRole: 'Standard' | 'Premium'): void {
    if (!newRole || newRole === user.role || this.updatingRoleUserId) {
      return;
    }

    const confirmed = window.confirm(
      `¿Cambiar el rol de ${user.email} de ${user.role} a ${newRole}?`,
    );

    if (!confirmed) {
      // reload to restore previous select value
      this.getUsersByAllied();
      return;
    }

    this.updatingRoleUserId = user._id;

    this.subs.sink = this.userAlliedService
      .updateUserAlliedRole(user._id, { role: newRole })
      .subscribe({
        next: () => {
          this.updatingRoleUserId = null;
          this._snackBar.open(
            `Rol actualizado a ${newRole}`,
            'cerrar',
            {
              duration: 3000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
            },
          );
        },
        error: (error) => {
          this.updatingRoleUserId = null;
          this.getUsersByAllied();

          const backendMessage =
            error?.error?.message ||
            error?.error?.errors?.[0]?.message ||
            'Ups! error al actualizar el rol';

          this._snackBar.open(backendMessage, 'cerrar', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: 'my-snackbar-error',
          });
        },
      });
  }

  public toggleUserStatus(user: IUserAllied): void {
    if (this.processingUserId) {
      return;
    }

    this.processingUserId = user._id;

    this.subs.sink = this.userAlliedService
      .updateUserAlliedStatus(user._id, { active: !user.active })
      .subscribe({
        next: () => {
          this.processingUserId = null;

          this._snackBar.open(
            user.active
              ? 'Cuenta inhabilitada con éxito'
              : 'Cuenta habilitada con éxito',
            'cerrar',
            {
              duration: 3000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
            },
          );
        },
        error: (error) => {
          this.processingUserId = null;

          const backendMessage =
            error?.error?.message ||
            error?.error?.errors?.[0]?.message ||
            'Ups! error al actualizar el estado de la cuenta';

          this._snackBar.open(backendMessage, 'cerrar', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: 'my-snackbar-error',
          });
        },
      });
  }
}
